import {
  App,
  Button,
  Card,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import {
  inventoryApi,
  type InventoryItem,
  type InventoryItemInput,
  type InventoryPurchase,
  type InventoryIssue,
} from '@/api/inventory';
import { financeApi } from '@/api/finance';
import { setupApi } from '@/api/setup';
import { hrApi } from '@/api/hr';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function ConsumablesPage() {
  const [search, setSearch] = useSearchParams();
  const tab = search.get('tab') ?? 'items';

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Inventory"
        title="Consumables"
        description="Office and lab supplies — stock-in (purchases) and stock-out (issues)."
      />
      <Tabs
        activeKey={tab}
        onChange={(k) => setSearch({ tab: k })}
        items={[
          { key: 'items', label: 'Items', children: <ConsumableItemsTab /> },
          { key: 'purchases', label: 'Purchases', children: <ConsumablePurchasesTab /> },
          { key: 'issues', label: 'Issues', children: <ConsumableIssuesTab /> },
        ]}
      />
    </div>
  );
}

interface ItemFormShape {
  name: string;
  itemTypeId: number;
  unit: string;
  openingBalance: number;
  lowStockThreshold: number;
}

function ConsumableItemsTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('INVENTORY:EDIT'));

  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<ItemFormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', 'consumables', 'items'],
    queryFn: () => inventoryApi.listConsumables(),
  });

  const { data: types } = useQuery({
    queryKey: ['setup', 'lookups', 'store-item-types', 'active'],
    queryFn: () => setupApi.listLookup('store-item-types', true),
    staleTime: 5 * 60_000,
  });

  const upsert = useMutation({
    mutationFn: (v: ItemFormShape) => {
      const body: InventoryItemInput = {
        name: v.name.trim(),
        itemTypeId: v.itemTypeId,
        unit: v.unit.trim(),
        openingBalance: Number(v.openingBalance).toFixed(2),
        lowStockThreshold: Number(v.lowStockThreshold).toFixed(2),
      };
      return editing ? inventoryApi.updateConsumable(editing.id, body) : inventoryApi.createConsumable(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Item updated' : 'Item created');
      qc.invalidateQueries({ queryKey: ['inventory', 'consumables'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ unit: 'PCS', openingBalance: 0, lowStockThreshold: 5 });
  };
  const openEdit = (row: InventoryItem) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      itemTypeId: row.itemTypeId,
      unit: row.unit,
      openingBalance: Number(row.openingBalance),
      lowStockThreshold: Number(row.lowStockThreshold),
    });
  };
  const open = creating || editing != null;

  const columns: TableProps<InventoryItem>['columns'] = [
    {
      title: 'Item',
      render: (_, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.name}</Typography.Text>
          <Space size={6}>
            {row.itemTypeName && <Tag>{row.itemTypeName}</Tag>}
            <Tag>{row.unit}</Tag>
            {row.lowStock && <Tag color="warning" icon={<WarningOutlined />}>Low</Tag>}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Stock',
      dataIndex: 'currentStock',
      width: 130,
      align: 'right',
      render: (v: string, row) => (
        <Typography.Text strong>{PKR(v)} {row.unit}</Typography.Text>
      ),
    },
    {
      title: 'Threshold',
      dataIndex: 'lowStockThreshold',
      width: 110,
      align: 'right',
      render: (v: string) => PKR(v),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {canCreate && (
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New consumable
          </Button>
        </Space>
      )}

      {data && data.length === 0 && !isLoading ? (
        <Empty description="No consumable items yet" />
      ) : (
        <Table rowKey="id" size="middle" loading={isLoading} columns={columns} dataSource={data ?? []} pagination={{ pageSize: 25 }} />
      )}

      <Drawer
        title={editing ? `Edit · ${editing.name}` : 'New consumable item'}
        open={open}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        size={460}
        destroyOnHidden
        extra={
          <Space>
            <Button onClick={() => { setEditing(null); setCreating(false); }}>Cancel</Button>
            <Button type="primary" loading={upsert.isPending} onClick={() => form.submit()}>
              {editing ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(v) => upsert.mutate(v)}>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 200 }]}>
              <Input autoFocus placeholder="e.g. A4 paper ream" />
            </Form.Item>
            <Form.Item label="Type" name="itemTypeId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(types ?? []).map((t) => ({ value: t.id, label: t.name }))}
              />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Unit" name="unit" rules={[{ required: true, max: 20 }]} style={{ width: 130 }}>
                <Input placeholder="PCS / REAM / L" />
              </Form.Item>
              <Form.Item
                label="Opening balance"
                name="openingBalance"
                rules={[{ required: true, type: 'number', min: 0 }]}
                style={{ flex: 1 }}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  step={5}
                  min={0}
                  disabled={!!editing && Number(editing.currentStock) !== Number(editing.openingBalance)}
                />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Low-stock threshold" name="lowStockThreshold" rules={[{ required: true, type: 'number', min: 0 }]}>
              <InputNumber style={{ width: '100%' }} step={1} min={0} />
            </Form.Item>
          </Form>
        )}
      </Drawer>
    </div>
  );
}

interface PurchaseFormShape {
  inventoryItemId: number;
  vendorId?: number;
  purchaseDate: Dayjs;
  quantity: number;
  unitCost?: number;
  paymentType: 'CASH' | 'CREDIT';
  bankAccountId?: number;
  expenseAccountId: number;
  remarks?: string;
}

function ConsumablePurchasesTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm<PurchaseFormShape>();

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['inventory', 'consumables', 'purchases', params],
    queryFn: () => inventoryApi.listConsumablePurchases(params.from, params.to),
  });

  const { data: items } = useQuery({
    queryKey: ['inventory', 'consumables', 'items'],
    queryFn: inventoryApi.listConsumables,
    staleTime: 60_000,
  });

  const { data: vendors } = useQuery({
    queryKey: ['inventory', 'vendors'],
    queryFn: inventoryApi.listVendors,
    staleTime: 60_000,
  });

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts', 'active'],
    queryFn: () => financeApi.listBankAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const banks = (banksQuery.data ?? []).filter((b) => b.active);

  const accountsQuery = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const expenseAccounts = (accountsQuery.data ?? []).filter(
    (a) => a.accountType === 'EXPENSE' && a.postingAccount && a.active,
  );

  const watchedPaymentType = Form.useWatch('paymentType', form);

  const add = useMutation({
    mutationFn: (v: PurchaseFormShape) =>
      inventoryApi.addConsumablePurchase({
        inventoryItemId: v.inventoryItemId,
        vendorId: v.vendorId ?? null,
        purchaseDate: v.purchaseDate.format('YYYY-MM-DD'),
        quantity: Number(v.quantity).toFixed(2),
        unitCost: v.unitCost != null ? Number(v.unitCost).toFixed(2) : null,
        paymentType: v.paymentType,
        bankAccountId: v.paymentType === 'CASH' ? v.bankAccountId : null,
        expenseAccountId: v.expenseAccountId,
        remarks: v.remarks?.trim() || undefined,
      }),
    onSuccess: () => {
      message.success('Stock-in recorded · DR Expense · CR Cash/Vendor');
      qc.invalidateQueries({ queryKey: ['inventory', 'consumables'] });
      setAdding(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const columns: TableProps<InventoryPurchase>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'purchaseDate',
      width: 110,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    { title: 'Item', dataIndex: 'inventoryItemName' },
    { title: 'Vendor', dataIndex: 'vendorName', render: (n: string | null) => n ?? '—' },
    { title: 'Qty', dataIndex: 'quantity', width: 90, align: 'right' },
    {
      title: 'Cost',
      dataIndex: 'totalCost',
      width: 130,
      align: 'right',
      render: (v: string | null) => v ? `PKR ${PKR(v)}` : '—',
    },
    { title: 'Remarks', dataIndex: 'remarks', render: (r: string | null) => r ?? '—' },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields();
            form.setFieldsValue({ purchaseDate: dayjs() });
            setAdding(true);
          }}>
            Stock in
          </Button>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No purchases in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table rowKey="id" size="middle" loading={isFetching} columns={columns} dataSource={data ?? []} pagination={{ pageSize: 25 }} />
        </Card>
      )}

      <Modal
        title="Stock in (consumable)"
        open={adding}
        onCancel={() => setAdding(false)}
        onOk={() => form.submit()}
        confirmLoading={add.isPending}
        okText="Record"
        destroyOnHidden
      >
        {adding && (
          <Form form={form} layout="vertical" onFinish={(v) => add.mutate(v)}>
            <Form.Item label="Item" name="inventoryItemId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(items ?? []).map((i) => ({ value: i.id, label: i.name }))}
              />
            </Form.Item>
            <Form.Item label="Vendor (optional)" name="vendorId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(vendors ?? []).map((v) => ({ value: v.id, label: v.name }))}
              />
            </Form.Item>
            <Form.Item label="Date" name="purchaseDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                label="Quantity"
                name="quantity"
                rules={[{ required: true, type: 'number', min: 0.01 }]}
                style={{ flex: 1 }}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={5} />
              </Form.Item>
              <Form.Item label="Unit cost" name="unitCost" rules={[{ required: true, type: 'number', min: 0 }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: '100%' }} min={0} step={50} />
              </Form.Item>
            </Space.Compact>
            <Form.Item
              label="Payment"
              name="paymentType"
              rules={[{ required: true }]}
              initialValue="CASH"
              tooltip="Cash → DR Expense / CR Cash. Credit → DR Expense / CR Vendor Payable."
            >
              <Select
                options={[
                  { value: 'CASH', label: 'Cash (paid now)' },
                  { value: 'CREDIT', label: 'Credit (to vendor)' },
                ]}
              />
            </Form.Item>
            {watchedPaymentType === 'CASH' && (
              <Form.Item
                label="Pay from"
                name="bankAccountId"
                rules={[{ required: true, message: 'Required for cash purchase' }]}
              >
                <Select
                  loading={banksQuery.isLoading}
                  placeholder="Cash drawer or bank"
                  options={banks.map((b) => ({
                    value: b.id,
                    label: `${b.accountName}${b.bankName ? ` · ${b.bankName}` : ''}${b.cash ? ' (cash)' : ''}`,
                  }))}
                />
              </Form.Item>
            )}
            <Form.Item
              label="Posts to (expense account)"
              name="expenseAccountId"
              rules={[{ required: true, message: 'Required — drives the trial balance' }]}
              tooltip="DR side. e.g. 5301 Stationery & Supplies."
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={accountsQuery.isLoading}
                options={expenseAccounts.map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }))}
              />
            </Form.Item>
            <Form.Item label="Remarks" name="remarks" rules={[{ max: 2000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}

interface IssueFormShape {
  inventoryItemId: number;
  issuedToStaffId?: number;
  issuedToDescription?: string;
  issueDate: Dayjs;
  quantity: number;
  purpose: string;
}

function ConsumableIssuesTab() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('INVENTORY:CREATE'));

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [issuing, setIssuing] = useState(false);
  const [form] = Form.useForm<IssueFormShape>();

  const params = { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') };
  const { data, isFetching } = useQuery({
    queryKey: ['inventory', 'consumables', 'issues', params],
    queryFn: () => inventoryApi.listConsumableIssues(params.from, params.to),
  });

  const { data: items } = useQuery({
    queryKey: ['inventory', 'consumables', 'items'],
    queryFn: inventoryApi.listConsumables,
    staleTime: 60_000,
  });

  const { data: staffPage } = useQuery({
    queryKey: ['hr', 'staff', 'active', 'all'],
    queryFn: () => hrApi.searchStaff({ active: true, size: 500 }),
    staleTime: 60_000,
  });
  const staff = staffPage?.rows ?? [];

  const issue = useMutation({
    mutationFn: (v: IssueFormShape) =>
      inventoryApi.addConsumableIssue({
        inventoryItemId: v.inventoryItemId,
        issuedToStaffId: v.issuedToStaffId,
        issuedToDescription: v.issuedToDescription?.trim() || undefined,
        issueDate: v.issueDate.format('YYYY-MM-DD'),
        quantity: Number(v.quantity).toFixed(2),
        purpose: v.purpose.trim(),
      }),
    onSuccess: () => {
      message.success('Issue recorded');
      qc.invalidateQueries({ queryKey: ['inventory', 'consumables'] });
      setIssuing(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const columns: TableProps<InventoryIssue>['columns'] = [
    {
      title: 'Date',
      dataIndex: 'issueDate',
      width: 110,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    { title: 'Item', dataIndex: 'inventoryItemName' },
    {
      title: 'To',
      render: (_, row) =>
        row.issuedToStaffName ?? row.issuedToDescription ?? <span style={{ opacity: 0.4 }}>—</span>,
    },
    { title: 'Qty', dataIndex: 'quantity', width: 90, align: 'right' },
    { title: 'Purpose', dataIndex: 'purpose' },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Space wrap size={12}>
        <DatePicker.RangePicker
          value={range}
          onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])}
          format="DD MMM YYYY"
          allowClear={false}
        />
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields();
            form.setFieldsValue({ issueDate: dayjs() });
            setIssuing(true);
          }}>
            Issue out
          </Button>
        )}
      </Space>

      {data && data.length === 0 && !isFetching ? (
        <Empty description="No issues in this range" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table rowKey="id" size="middle" loading={isFetching} columns={columns} dataSource={data ?? []} pagination={{ pageSize: 25 }} />
        </Card>
      )}

      <Modal
        title="Issue consumable"
        open={issuing}
        onCancel={() => setIssuing(false)}
        onOk={() => form.submit()}
        confirmLoading={issue.isPending}
        okText="Issue"
        destroyOnHidden
      >
        {issuing && (
          <Form form={form} layout="vertical" onFinish={(v) => issue.mutate(v)}>
            <Form.Item label="Item" name="inventoryItemId" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={(items ?? [])
                  .filter((i) => Number(i.currentStock) > 0)
                  .map((i) => ({
                    value: i.id,
                    label: `${i.name} · stock ${PKR(i.currentStock)} ${i.unit}`,
                  }))}
              />
            </Form.Item>
            <Form.Item label="To staff (optional)" name="issuedToStaffId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={staff.map((s) => ({ value: s.id, label: `${s.firstName} ${s.lastName}` }))}
              />
            </Form.Item>
            <Form.Item
              label="Or description"
              name="issuedToDescription"
              rules={[{ max: 200 }]}
              tooltip="Use if issuing to a department or lab, not a person."
            >
              <Input placeholder="e.g. Chemistry lab" />
            </Form.Item>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item label="Date" name="issueDate" rules={[{ required: true }]} style={{ flex: 1 }}>
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
              <Form.Item
                label="Quantity"
                name="quantity"
                rules={[{ required: true, type: 'number', min: 0.01 }]}
                style={{ width: 160 }}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={1} />
              </Form.Item>
            </Space.Compact>
            <Form.Item label="Purpose" name="purpose" rules={[{ required: true, max: 2000 }]}>
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} placeholder="What's it for?" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
